import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { emitApiError } from "@/api/apiErrorStore";
import { useChatStream } from "@/api/useChatStream";
import { useAgents } from "@/features/agents/useAgents";
import { useUpdateSessionCache } from "@/features/chat/useChatMessages";
import { useCreateSession, useSessions } from "@/features/sessions/useSessions";
import type { Agent, MessageCreate, Session, UploadedFile } from "@/types";

interface ChatContextValue {
  activeSession: Session | undefined;
  grantedAgents: Agent[];
  selectedAgentId: string;
  setSelectedAgentId: (id: string) => void;
  isStreaming: boolean;
  streamedText: string;
  agentEvents: unknown[];
  pendingUserMessage: string | null;
  isSending: boolean;
  send: (content: string, attachments: UploadedFile[]) => void;
  startNewSession: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

/** Shared chat state (streaming + session/agent selection) consumed by the docked composer and
 * the conversation thread. Sending a message with no active session creates one first (legacy
 * "start a session by simply sending a message"). */
export function ChatProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const activeSessionId = location.pathname.startsWith("/chat/")
    ? decodeURIComponent(location.pathname.slice("/chat/".length))
    : null;

  const { data: sessions } = useSessions();
  const activeSession = activeSessionId
    ? (sessions ?? []).find((session) => session.id === activeSessionId)
    : undefined;

  const { data: agentsPage } = useAgents({ limit: 100 });
  const grantedAgents = useMemo(
    () =>
      (agentsPage?.items ?? []).filter((agent) => agent.access === "granted"),
    [agentsPage],
  );

  const [selectedAgentId, setSelectedAgentId] = useState("");
  useEffect(() => {
    if (!selectedAgentId && grantedAgents.length > 0) {
      setSelectedAgentId(grantedAgents[0]!.slug);
    }
  }, [grantedAgents, selectedAgentId]);

  // Optimistically shown as a user bubble while the agent is thinking/streaming, so the sender
  // sees their message immediately instead of only once `done` repopulates the session cache.
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null,
  );

  const updateCache = useUpdateSessionCache();
  const stream = useChatStream(activeSessionId ?? "", (updated) => {
    updateCache(updated);
    setPendingUserMessage(null);
  });
  const createSession = useCreateSession();

  // Clear the optimistic bubble if the stream fails (otherwise it would linger).
  useEffect(() => {
    if (stream.error) setPendingUserMessage(null);
  }, [stream.error]);

  // Surface stream errors through the global notification centre (same handling as everything else).
  useEffect(() => {
    if (stream.error) emitApiError(stream.error);
  }, [stream.error]);

  // First message of a brand-new session is stashed, then sent once the route (and the stream
  // hook's sessionId) has switched to the freshly created session.
  const [pending, setPending] = useState<MessageCreate | null>(null);
  const sendRef = useRef(stream.send);
  sendRef.current = stream.send;
  useEffect(() => {
    if (activeSessionId && pending) {
      const payload = pending;
      setPending(null);
      void sendRef.current(payload);
    }
  }, [activeSessionId, pending]);

  const send = useCallback(
    (content: string, attachments: UploadedFile[]) => {
      const payload: MessageCreate = {
        content,
        uploadedFileIds: attachments.map((file) => file.id),
      };
      setPendingUserMessage(content);
      if (activeSession) {
        void stream.send(payload);
        return;
      }
      if (!selectedAgentId) return;
      createSession.mutate(selectedAgentId, {
        onSuccess: (session) => {
          setPending(payload);
          navigate(`/chat/${session.id}`);
        },
      });
    },
    [activeSession, selectedAgentId, stream, createSession, navigate],
  );

  const startNewSession = useCallback(() => {
    navigate("/chat");
  }, [navigate]);

  const value: ChatContextValue = {
    activeSession,
    grantedAgents,
    selectedAgentId,
    setSelectedAgentId,
    isStreaming: stream.isStreaming,
    streamedText: stream.streamedText,
    agentEvents: stream.agentEvents,
    pendingUserMessage,
    isSending: createSession.isPending || stream.isStreaming,
    send,
    startNewSession,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useChat(): ChatContextValue {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
